import {
  Typography,
  TextField,
  Select,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Stack,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { supportedLocales } from '@/i18n/i18n';
import type { TranslationKey } from '@/i18n/translation-key';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { accordionSummaryStyles } from './general-tab.constants';
import type { UpdateUser } from './general-tab.types';
import { ClerkProfilePhotoEditor } from '@/components/nav-bar/clerk-profile-photo-editor';

interface UserInfoFormProps {
  form: UseFormReturn<UpdateUser>;
  onSubmit: () => void;
  onCancel: () => void;
}

export const UserInfoForm = ({ form, onSubmit, onCancel }: UserInfoFormProps) => {
  const t = useTranslations();
  const language = useWatch({ control: form.control, name: 'general.language' });

  return (
    <Accordion defaultExpanded sx={{ backgroundColor: 'transparent' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles}>
        <Typography>{t('user.profile.userInfo.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ py: 0 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, mt: 0.5 }}>
          <ClerkProfilePhotoEditor avatarSize={80} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">{t('user.profile.userInfo.profilePhoto')}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('user.profile.userInfo.profilePhotoHint')}
            </Typography>
          </Box>
        </Stack>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              variant="standard"
              label={t('user.profile.userInfo.firstName')}
              {...form.register('general.firstName')}
              error={!!form.formState.errors.general?.firstName}
              helperText={form.formState.errors.general?.firstName?.message}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              variant="standard"
              label={t('user.profile.userInfo.lastName')}
              {...form.register('general.lastName')}
              error={!!form.formState.errors.general?.lastName}
              helperText={form.formState.errors.general?.lastName?.message}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              variant="standard"
              label={t('user.profile.userInfo.phoneNumber')}
              type="tel"
              {...form.register('general.phoneNumber')}
              error={!!form.formState.errors.general?.phoneNumber}
              helperText={form.formState.errors.general?.phoneNumber?.message}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth variant="standard">
              <InputLabel id="language-select-label">{t('user.profile.userInfo.language')}</InputLabel>
              <Select
                labelId="language-select-label"
                value={language ?? ''}
                onChange={(e) => form.setValue('general.language', e.target.value, { shouldDirty: true })}
                data-testid="locale-switch"
                size="medium"
                sx={{ mt: '16px' }}
              >
                {supportedLocales.map((locale) => (
                  <MenuItem key={locale} value={locale}>
                    {t(`languages.${locale}` as TranslationKey)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, mb: 1 }}>
          <Button onClick={onCancel} variant="outlined" size="small" disabled={!form.formState.isDirty}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onSubmit} variant="contained" size="small" disabled={!form.formState.isDirty}>
            {t('common.save')}
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
